import React from "react";
import "./BMICalculatorContent.css";

const BMICalculatorContent = () => {
  const handleScroll = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="bmi-calculator-container" id="bmi-formula">
      <div className="bmi-calculator">
        <h2>BMI Formula: Understanding the Body Mass Index Calculation</h2>
        <p className="bmi-calculator-description">
          The Body Mass Index (BMI) Calculator helps determine whether your weight
          falls within a healthy range based on your height and weight. It offers
          valuable insights into your overall health, helping you make informed
          decisions to improve your lifestyle. Here's how to calculate BMI value
          accurately.
        </p>

        <h2>BMI Calculation Formula: How to Calculate BMI Value</h2>
        <p className="bmi-calculator-description">
          BMI is a simple and widely-used tool to estimate body fat and assess
          whether you're in a healthy weight range. It's calculated by dividing
          your weight by the square of your height. This formula can be applied
          using two types of measurement systems: the metric system and the
          imperial system. Below is a detailed explanation of how to calculate BMI
          value for individuals of various body types, including BMI for women.
        </p>

        <h3>Metric Units (kg/m<sup>2</sup>):</h3>
        <ul>
          <li>BMI = weight (kg) ÷ height<sup>2</sup> (m)</li>
        </ul>
        <p>
          To calculate BMI in the metric system, you will need your weight in
          kilograms and height in meters. Square your height (multiply it by
          itself) and then divide your weight by this value. This will give you
          your BMI.
        </p>

        <h3>Imperial Units (lb/in<sup>2</sup>):</h3>
        <ul>
          <li>BMI = weight (lb) ÷ height<sup>2</sup> (in) × 703</li>
        </ul>
        <p>
          If you're using the imperial system (pounds and inches), first convert
          your weight into pounds and your height into inches. Square your height
          in inches, then divide your weight by this number and multiply by 703 to
          get the BMI value.
        </p>

        <h3>How to Calculate BMI of a Person: A Step-by-Step Guide</h3>
        <p>To calculate BMI for a person, follow these steps:</p>
        <ol>
          <li>Measure the person's weight in kilograms (kg) or pounds (lb).</li>
          <li>Measure the person's height in meters (m) or inches (in).</li>
          <li>Use the appropriate formula based on the unit of measurement.</li>
          <li>Square the height measurement (height × height).</li>
          <li>Divide the weight by the squared height to get the BMI value.</li>
        </ol>
        <p>
          Remember that BMI is a helpful screening tool but does not directly
          measure body fat. It should be used alongside other measurements like
          waist-to-hip ratio, and health assessments for a full picture of your
          health.
        </p>

        <h3>BMI Calculator for Women</h3>
        <p>
          While the general formula for calculating BMI applies to both men and
          women, it's important to note that women tend to have a higher
          percentage of body fat compared to men at the same BMI value.
          Understanding how BMI works for women can provide insights into overall
          health, especially when combined with other factors like muscle mass,
          body composition, and lifestyle.
        </p>

        <h3>Limitations of BMI</h3>
        <p>
          While BMI is a widely used and effective tool for assessing general health,
          it is not without limitations. It does not differentiate between muscle and
          fat mass, and it may not provide an accurate picture for athletes, older adults,
          or people with varying body compositions. Here are some considerations to keep in mind:
        </p>
        <ul>
          <li>
            <strong>Muscle Mass:</strong> Athletes or individuals with a high muscle mass may have a high BMI
            but low body fat percentage. BMI might overestimate health risks in such cases.
          </li>
          <li>
            <strong>Age and Gender:</strong> BMI does not account for differences in body fat distribution based
            on age or gender. Women tend to have more body fat than men at the same BMI.
          </li>
          <li>
            <strong>Ethnicity:</strong> Research suggests that BMI-related health risks vary across ethnic groups.
            For instance, people of Asian descent may face health risks at lower BMI levels.
          </li>
        </ul>

        <h3>Additional Methods to Assess Health</h3>
        <p>
          To get a comprehensive view of your health, consider using these additional
          methods alongside BMI. While BMI is a valuable tool, it is not a one-size-fits-all
          metric, and combining it with other assessments provides a clearer picture of overall health.
        </p>
        <ul>
          <li>
            <strong>Waist-to-Hip Ratio:</strong> This measures the distribution of fat around your abdomen and hips.
            A high ratio indicates a higher risk of health issues, particularly heart disease and diabetes.
            <ul>
              <li>Formula: Waist circumference ÷ Hip circumference</li>
              <li>Healthy Range: For women, a ratio below 0.85; for men, below 0.90.</li>
              <li>Tip: Measure at the narrowest point of the waist and the widest point of the hips for accuracy.</li>
            </ul>
          </li>
          <li>
            <strong>Body Fat Percentage:</strong> This provides a more direct measure of body composition and
            differentiates between fat mass and lean body mass.
            <ul>
              <li>Methods: Dual-Energy X-ray Absorptiometry (DEXA), BIA, or calipers.</li>
              <li>Healthy Range: Varies by age and gender; typically 20-30% for women and 10-20% for men.</li>
              <li>Benefit: Offers insights into fitness levels and potential health risks.</li>
            </ul>
          </li>
          <li>
            <strong>Skinfold Thickness Measurements:</strong> This involves measuring the thickness of skinfolds
            at specific body sites to estimate body fat percentage.
            <ul>
              <li>Common Sites: Triceps, abdomen, thigh, and subscapular regions.</li>
              <li>Tool: Requires calipers and expertise for accurate measurement.</li>
              <li>Advantage: Provides localized fat distribution information.</li>
            </ul>
          </li>
          <li>
            <strong>Bioelectrical Impedance Analysis (BIA):</strong> A non-invasive method to estimate body composition
            by measuring electrical resistance through the body.
            <ul>
              <li>How It Works: Lean tissue conducts electricity better than fat tissue.</li>
              <li>Devices: Available as handheld devices, scales, or clinical equipment.</li>
              <li>Consideration: Hydration levels can affect accuracy, so follow preparation guidelines.</li>
            </ul>
          </li>
          <li>
            <strong>Resting Metabolic Rate (RMR):</strong> This metric assesses the number of calories your body burns
            at rest, providing insights into your metabolic health.
            <ul>
              <li>Method: Measured via indirect calorimetry or estimated using formulas like the Mifflin-St Jeor Equation.</li>
              <li>Use: Helps determine calorie needs for weight loss, maintenance, or gain.</li>
              <li>Tip: Combine RMR with activity levels for a full caloric expenditure profile.</li>
            </ul>
          </li>
          <li>
            <strong>Visceral Fat Measurement:</strong> Focuses on fat stored around internal organs, which poses a higher risk for metabolic diseases.
            <ul>
              <li>Measurement: Often assessed via imaging techniques like CT or MRI scans.</li>
              <li>Significance: High visceral fat is linked to greater risks of cardiovascular diseases and type 2 diabetes.</li>
              <li>Reduction Strategies: Include aerobic exercises, dietary changes, and stress management.</li>
            </ul>
          </li>
        </ul>
        <p>
          These methods, when used together with BMI, provide a holistic view of health. While some require professional equipment
          or expertise, others can be easily performed at home, making health tracking accessible for everyone.
        </p>

        <button
          className="bmi-calculator-button"
          onClick={() => handleScroll("bmi-categories")}
        >
          Explore BMI Categories
        </button>
      </div>

      <div id="healthy-habits" className="bmi-calculator">
        <div id="bmi-categories">
          <h2>BMI Categories and Their Health Impacts</h2>

          <h3>Underweight (BMI &lt; 18.5)</h3>
          <p>
            Being underweight can result from malnutrition or underlying health
            conditions. It is linked to risks such as weakened immunity, osteoporosis,
            fatigue, and nutrient deficiencies.
          </p>
          <p>Steps for Improvement:</p>
          <ul>
            <li>
              <strong>Increase Caloric Intake:</strong> Add calorie-dense, nutrient-rich foods to your meals,
              such as nuts, avocados, whole grains, and dairy products.
            </li>
            <li>
              <strong>Focus on Nutrient Balance:</strong> Include a mix of macronutrients—carbohydrates, proteins,
              and healthy fats—for balanced weight gain.
            </li>
            <li>
              <strong>Consult a Healthcare Provider:</strong> Seek personalized guidance to address potential
              underlying conditions and develop a tailored nutrition plan.
            </li>
            <li>
              <strong>Strength-Building Exercises:</strong> Engage in resistance training to gain lean muscle mass
              and improve overall strength.
            </li>
          </ul>

          <h3>Normal Weight (BMI 18.5–24.9)</h3>
          <p>
            A normal BMI range is associated with good overall health. Maintaining this
            range requires consistent attention to lifestyle habits, including diet,
            physical activity, and mental well-being.
          </p>
          <p>Key Habits to Maintain:</p>
          <ul>
            <li>
              <strong>Balanced Diet:</strong> Consume a diet rich in fruits, vegetables, whole grains, and lean proteins
              to support overall health and energy levels.
            </li>
            <li>
              <strong>Regular Exercise:</strong> Engage in at least 150 minutes of moderate physical activity or 75
              minutes of vigorous activity per week.
            </li>
            <li>
              <strong>Adequate Hydration:</strong> Ensure proper hydration by drinking water regularly throughout the day.
            </li>
            <li>
              <strong>Sleep and Recovery:</strong> Prioritize 7–9 hours of sleep each night to aid recovery and maintain
              metabolic balance.
            </li>
          </ul>

          <h3>Overweight (BMI 25–29.9)</h3>
          <p>
            Being overweight can increase the risk of developing chronic conditions such
            as type 2 diabetes, heart disease, and high blood pressure.
          </p>
          <p>Actions to Take:</p>
          <ul>
            <li>
              <strong>Portion Control:</strong> Monitor portion sizes and avoid overeating by being mindful during meals.
            </li>
            <li>
              <strong>Healthy Food Choices:</strong> Focus on nutrient-rich, minimally processed foods like vegetables,
              whole grains, and lean proteins.
            </li>
            <li>
              <strong>Regular Physical Activity:</strong> Incorporate daily exercises such as brisk walking, swimming,
              or cycling for at least 30 minutes.
            </li>
            <li>
              <strong>Limit Unhealthy Foods:</strong> Reduce the intake of processed foods, sugary beverages, and trans fats.
            </li>
          </ul>

          <h3>Obesity (BMI &gt; 30)</h3>
          <p>
            Obesity significantly raises the risk for severe health conditions, including cardiovascular diseases,
            diabetes, and certain cancers.
          </p>
          <p>Immediate Actions:</p>
          <ul>
            <li>
              <strong>Adopt a Balanced Diet:</strong> Create a calorie deficit through a sustainable diet plan
              focused on whole foods and reduced calorie intake.
            </li>
            <li>
              <strong>Increase Physical Activity:</strong> Aim for at least 300 minutes of moderate-intensity exercise
              weekly, incorporating both cardio and strength training.
            </li>
            <li>
              <strong>Seek Professional Help:</strong> Consult with a healthcare provider or nutritionist for
              personalized strategies and possible medical interventions.
            </li>
            <li>
              <strong>Join Support Groups:</strong> Participate in weight management programs or support groups to
              stay motivated and accountable.
            </li>
          </ul>
        </div>
        <div id="bmi-children">
          <h3>Special Considerations for Children and Adolescents</h3>
          <p>
            For children and adolescents, BMI is interpreted differently than for adults.
            BMI-for-age percentile charts are used, which account for age, gender, and developmental stages.
            These charts help identify potential weight concerns while recognizing that children are still growing.
          </p>
          <p>Why BMI-for-Age Percentile Charts Are Important:</p>
          <ul>
            <li>
              Children experience significant changes in body composition during various stages of development.
            </li>
            <li>
              Percentile charts help determine whether a child is underweight, at a healthy weight, overweight, or obese.
            </li>
            <li>
              Early identification of unhealthy weight trends can lead to timely interventions and better health outcomes.
            </li>
          </ul>
          <p>Key Actions for Supporting Healthy Growth:</p>
          <ul>
            <li>
              Provide a balanced diet rich in fruits, vegetables, whole grains, lean proteins, and healthy fats.
            </li>
            <li>
              Encourage regular physical activity appropriate for their age, aiming for at least 60 minutes of activity daily.
            </li>
            <li>
              Monitor screen time and promote active play and outdoor activities.
            </li>
            <li>
              Regularly consult healthcare professionals to track growth and address concerns proactively.
            </li>
            <li>
              Foster a positive relationship with food, avoiding restrictive or punitive approaches to eating habits.
            </li>
          </ul>
        </div>
        <h3>Future Trends in BMI and Health Assessment</h3>
        <p>
          As technology advances, traditional BMI measurement may be supplemented or replaced by more precise health metrics.
          Innovations in wearable devices and AI-driven health assessments are on the horizon, promising real-time insights
          into body composition, metabolic rates, and overall wellness.
        </p>
        <ul>
          <li>
            <strong>Wearable Technology:</strong> Devices equipped with sensors to monitor body fat percentage,
            hydration levels, and metabolic rates.
          </li>
          <li>
            <strong>AI in Healthcare:</strong> Machine learning algorithms analyzing patterns in health data
            to predict risks and recommend personalized interventions.
          </li>
          <li>
            <strong>Genetic Profiling:</strong> Understanding genetic predispositions to obesity and related
            conditions for more targeted health advice.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default BMICalculatorContent;
